import { useMemo } from 'react'

import type { ToolbarEmailListItem } from '#/shared/api/generated/shipment/models'
import { useListItemsApiV1ToolbarTypeGet } from '#/shared/api/generated/shipment/theCargoShipment'
import { formatNoteTimestamp } from '#/shared/lib/format'

const DEFAULT_EMAIL_FEED_SIZE = 100

export const EMAIL_DIRECTION = {
  inbound: 'inbound',
  outbound: 'outbound',
} as const

export interface ShipmentEmailFeedItem {
  id: string
  direction: string
  subject: string
  body: string
  sender: string | undefined
  recipient: string | undefined
  deliveredAt: string | undefined
  openedAt: string | undefined
  isApp: boolean
  timestamp: string
  occurredAt: string
}

export function isEmailDelivered(email: ShipmentEmailFeedItem): boolean {
  return Boolean(email.deliveredAt ?? email.openedAt)
}

export function isEmailOpened(email: ShipmentEmailFeedItem): boolean {
  return Boolean(email.openedAt)
}

interface UseShipmentEmailFeedOptions {
  enabled?: boolean
  size?: number
}

export interface UseShipmentEmailFeedResult {
  emails: ShipmentEmailFeedItem[]
  isLoading: boolean
}

function pickTimestamp(email: ToolbarEmailListItem): string {
  return email.delivered_at ?? email.opened_at ?? ''
}

export function toShipmentEmailFeedItem(
  email: ToolbarEmailListItem,
): ShipmentEmailFeedItem {
  const ts = pickTimestamp(email)
  return {
    id: email.id,
    direction: email.direction,
    subject: email.subject ?? '',
    body: email.body ?? '',
    sender: email.sender ?? undefined,
    recipient: email.recipient ?? undefined,
    deliveredAt: email.delivered_at ?? undefined,
    openedAt: email.opened_at ?? undefined,
    isApp: email.is_app ?? false,
    timestamp: ts ? formatNoteTimestamp(ts) : '',
    occurredAt: ts,
  }
}

export function useShipmentEmailFeed(
  shipmentId: string,
  {
    enabled = true,
    size = DEFAULT_EMAIL_FEED_SIZE,
  }: UseShipmentEmailFeedOptions = {},
): UseShipmentEmailFeedResult {
  const query = useListItemsApiV1ToolbarTypeGet(
    'email',
    { shipment_id: shipmentId, size },
    { query: { enabled: enabled && Boolean(shipmentId) } },
  )

  const emails = useMemo<ShipmentEmailFeedItem[]>(() => {
    const items = (query.data?.data.items ??
      []) as unknown as ToolbarEmailListItem[]
    return items.map(toShipmentEmailFeedItem)
  }, [query.data])

  return {
    emails,
    isLoading: query.isLoading && enabled && Boolean(shipmentId),
  }
}
